import React, { useState, useEffect } from 'react';
import {
    View,
    Heading,
    Text,
    Button,
    Flex,
    Card,
    Loader,
    useTheme
} from '@aws-amplify/ui-react';
import { HardDrive, Check, X, CreditCard } from 'lucide-react';
import { formatFileSize } from '../utils/formatters';
import { getStorageTierConfigs, StorageTierConfig } from '../services/storageTierService';
import PaymentButton from './PaymentButton';
import { useStripe } from '../StripeContext';
import StripeCheckoutButton from './StripeCheckoutButton';
import { StorageTier } from '../API';


const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem'
};

const upgradeModalStyle: React.CSSProperties = {
    backgroundColor: '#1A1A1A',
    borderRadius: '8px',
    maxHeight: '90vh',
    overflowY: 'auto',
    border: '1px solid #303030'
};

// Force all cards to have consistent height
const cardContainerStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex'
};

const cardStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '8px',
    width: '100%' // Ensure full width
};

const cardContentStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
};

// Tier colors mapping
const tierColorMap: Record<string, string> = {
    FREE: '#4A5568',      // Gray
    BASIC: '#38A169',     // Green
    PREMIUM: '#3182CE',   // Blue
    PROFESSIONAL: '#6B46C1', // Purple
    ENTERPRISE: '#DD6B20'  // Orange
};

// Default color for any new tiers
const defaultTierColor = '#4A5568';

interface StorageUpgradeModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentTier: string;
    fromUploadError?: boolean;
    onUpgradeComplete?: () => void;
}

const StorageUpgradeModal: React.FC<StorageUpgradeModalProps> = ({
    isOpen,
    onClose,
    currentTier,
    fromUploadError = false,
    onUpgradeComplete
}) => {
    const { tokens } = useTheme();
    const [tierConfigs, setTierConfigs] = useState<StorageTierConfig[]>([]);
    const [loading, setLoading] = useState(true);

    const handleCheckoutCompleted = () => {
        if (onUpgradeComplete) {
          onUpgradeComplete();
        }
      };

    useEffect(() => {
        if (!isOpen) return;

        const loadTierConfigs = async () => {
            try {
                setLoading(true);
                const configs = await getStorageTierConfigs(true); // Force refresh
                setTierConfigs(configs.sort((a, b) => a.pricePerMonth - b.pricePerMonth));
            } catch (error) {
                console.error('Error loading tier configs:', error);
            } finally {
                setLoading(false);
            }
        };

        loadTierConfigs();
    }, [isOpen]);

    if (!isOpen) return null;

    if (loading) {
        return (
            <div style={modalOverlayStyle}>
                <Card width="95%" maxWidth="1200px" style={upgradeModalStyle} padding="2rem">
                    <Flex direction="column" alignItems="center" gap="1rem">
                        <Loader size="large" />
                        <Text color="white">Loading storage plans...</Text>
                    </Flex>
                </Card>
            </div>
        );
    }

    // Find the plan with the most features to standardize heights
    const maxFeatures = Math.max(...tierConfigs.map(plan => plan.features.length));

    return (
        <div style={modalOverlayStyle}>
            <Card width="95%" maxWidth="1200px" style={upgradeModalStyle}>
                <Flex direction="row" justifyContent="space-between" alignItems="center" padding="1.5rem">
                    <Heading level={3} color="white">
                        <Flex alignItems="center" gap="0.5rem">
                            <HardDrive size={24} />
                            <span>Upgrade Storage Plan</span>
                        </Flex>
                    </Heading>
                    <Button
                        variation="link"
                        onClick={onClose}
                    >
                        <X size={24} color="white" />
                    </Button>
                </Flex>

                <Text padding="0 1.5rem" marginBottom="2rem" color="#DDDDDD">
                    Choose a plan that works for your storage needs. You can upgrade or downgrade at any time.
                </Text>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '1rem',
                    margin: '0 1.5rem 2rem 1.5rem',
                }}>
                    {tierConfigs.map(plan => {
                        
                        const isCurrentPlan = plan.tier === currentTier;
                        
                        const planColor = tierColorMap[plan.tier] || defaultTierColor;

                        return (
                            <Card
                                key={plan.id}
                                padding="1.5rem"
                                backgroundColor={isCurrentPlan ? `${planColor}22` : '#252525'}
                                borderColor={isCurrentPlan ? planColor : '#333333'}
                                borderWidth="2px"
                                style={cardStyle}
                            >
                                <div style={cardContentStyle}>
                                    <Heading level={4} marginBottom="0.5rem" color="white">{plan.name}</Heading>
                                    <Heading level={3} marginBottom="1rem" color={isCurrentPlan ? planColor : 'white'}>
                                        {plan.pricePerMonth === 0 ? 'Free' : `$${plan.pricePerMonth.toFixed(2)}/month`}
                                    </Heading>

                                    <Text fontWeight="bold" marginBottom="0.5rem" color="#DDDDDD">
                                        {formatFileSize(plan.storageBytes)} Storage
                                    </Text>

                                    <Flex direction="column" gap="0.5rem" marginTop="1.5rem" marginBottom="2rem">
                                        {plan.features.map((feature, index) => (
                                            <Flex key={index} alignItems="center" gap="0.5rem">
                                                <Check size={16} color="#00C48C" />
                                                <Text color="#AAAAAA">{feature}</Text>
                                            </Flex>
                                        ))}

                                        {/* Add empty placeholder rows to standardize height */}
                                        {Array(maxFeatures - plan.features.length).fill(0).map((_, index) => (
                                            <View key={`spacer-${index}`} height="28px" />
                                        ))}
                                    </Flex>

                                    {/* Spacer to push button to bottom */}
                                    <div style={{ flex: 1 }}></div>
                                </div>

                                {isCurrentPlan ? (
                                    // Current plan button - keep as is
                                    <Button
                                        width="100%"
                                        marginTop="auto"
                                        variation="primary"
                                        backgroundColor={planColor}
                                        color="white"
                                        onClick={onClose}
                                    >
                                        Current Plan
                                    </Button>
                                ) : (
                                    // For non-current plans, use the new PaymentButton
                                    <StripeCheckoutButton
                                        tier={plan.tier as StorageTier}
                                        text="Select Plan"
                                        variant="primary"
                                        backgroundColor={plan.tier === 'PREMIUM' ? "#3182CE" :
                                            plan.tier === 'PROFESSIONAL' ? "#6B46C1" : "#4A5568"}
                                        fullWidth={true}
                                        onCheckoutCompleted={handleCheckoutCompleted}
                                    />
                                )}
                            </Card>
                        );
                    })}
                </div>

            </Card>
        </div>
    );
};

export default StorageUpgradeModal;